/* eslint-disable react/button-has-type */
// React
import React, { useState } from 'react';
import PropTypes from 'prop-types';

// CYBS-UI
import CybsTabItem from '@cybs-ui/components/lib/tabItem';
import CybsTooltip from '@cybs-ui/components/lib/tooltip';

// VDS-React
import ButtonLabel from '@visa/vds-react/button-label-styled';
import ButtonAction from '@visa/vds-react/button-action-styled';
import ButtonText from '@visa/vds-react/button-text-styled';
import ButtonIcon from '@visa/vds-react/button-icon-styled';
import ButtonLink from '@visa/vds-react/button-link-styled';
import ButtonSplit from '@visa/vds-react/button-split-styled';
import Icon from '@visa/vds-react/icon-styled';

import stylesBuilder from './component.styles';

const CybsButton = React.forwardRef(
  (
    {
      colorScheme,
      dataTest,
      disabled,
      href,
      i18n,
      iconClass,
      iconLocation,
      iconType,
      isActive,
      isSelected,
      onClick,
      readOnly,
      resolution,
      tag,
      prenventIconFocus,
      tooltipCustomCSS,
      tooltipId,
      tooltipDelayOn,
      tooltipDirection,
      tooltipShowOn,
      variant,
      width,
      ...props
    },
    ref
  ) => {
    /* This code is for colorScheme controlling the VDS iconType of
      a variant WCAG 2.1/VPDS redesign has the only acceptable iconTypes
      as light and light-outline. No more support for light-fill.
    */
    if (variant === 'icon' && !iconType) {
      if (colorScheme === 'primary') {
        iconType = 'light';
      } else if (colorScheme === 'secondary') {
        iconType = 'light-outline';
      } else if (colorScheme === 'tertiary') {
        iconType = 'light';
      }
    }

    const ariaLabel = i18n.ariaLabel || i18n.label || i18n.tooltipLabel;
    const diffLabel = ariaLabel !== i18n.label;

    const {
      splitButtonStyles,
      iconButtonStyles,
      textButtonStyles,
      linkButtonStyles
    } = stylesBuilder({
      width,
      diffLabel
    });

    const commonProps = {
      colorScheme,
      disabled,
      onClick,
      readOnly,
      ref,
      ...props
    };
    let button;
    switch (variant) {
      case 'tab':
        button = (
          <CybsTabItem
            dataTest={`${dataTest}-cybsButton-tab`}
            i18n={i18n}
            isSelected={isSelected}
            iconClass={iconClass}
            href={href}
            tooltipCustomCSS={tooltipCustomCSS}
            tooltipDelayOn={tooltipDelayOn}
            tooltipDirection={tooltipDirection}
            tooltipId={tooltipId}
            tooltipShowOn={tooltipShowOn}
            {...commonProps}
          />
        );

        break;
      case 'split':
        button = (
          <ButtonSplit
            data-test={`${dataTest}-cybsButton-split`}
            {...commonProps}
            customCSS={splitButtonStyles}
          >
            <ButtonLabel aria-hidden={diffLabel || undefined}>
              {i18n.label}
            </ButtonLabel>
            {diffLabel && <span className="cybs-a11y-hidden">{ariaLabel}</span>}
            <ButtonAction>
              <Icon name={iconClass} resolution="tiny" />
            </ButtonAction>
          </ButtonSplit>
        );
        break;
      case 'icon': {
        const [showTooltip, setShowTooltip] = useState(false);
        const { colorScheme: colorSchemeRemoved, ...propsNoColorScheme } =
          commonProps;
        const buttonIconProps = {
          'data-test': `${dataTest}-cybsButton-icon`,
          label: i18n.label,
          iconType,
          ...propsNoColorScheme
        };

        // if the showOn props is focus or focus-hover then we need to control the component with hooks
        // in this case we will add our own event listeners to trigger the tooltip
        if (tooltipShowOn.includes('focus')) {
          buttonIconProps.onFocus = () => setShowTooltip(true);
          buttonIconProps.onBlur = () => setShowTooltip(false);
          buttonIconProps.onKeyDown = e => {
            if ((e.key === 'Escape' || e.keyCode === 27) && showTooltip) {
              e.nativeEvent.stopImmediatePropagation();
              setShowTooltip(false);
            }
            // eslint-disable-next-line no-unused-expressions
            props.onKeyDown?.(e);
          };
        }

        if (tooltipShowOn === 'focus-hover') {
          buttonIconProps.onMouseEnter = () => setShowTooltip(true);
          buttonIconProps.onMouseLeave = () => setShowTooltip(false);
        }
        const iconButton = (
          <ButtonIcon {...buttonIconProps} customCSS={iconButtonStyles}>
            <Icon name={iconClass} resolution={resolution} />
            <span className="cybs-a11y-hidden">{ariaLabel}</span>
          </ButtonIcon>
        );
        button =
          disabled || (readOnly && prenventIconFocus) ? (
            iconButton
          ) : (
            <CybsTooltip
              customCSS={tooltipCustomCSS}
              dataTest={dataTest}
              delayOn={tooltipDelayOn}
              direction={tooltipDirection}
              id={tooltipId}
              isShown={
                tooltipShowOn.includes('focus') ? showTooltip : undefined
              }
              label={
                i18n.tooltipLabel || i18n.label || i18n.ariaLabel || iconClass
              }
              showOn={tooltipShowOn}
            >
              {iconButton}
            </CybsTooltip>
          );
        break;
      }
      case 'text-icon':
        button = (
          <ButtonIcon
            data-test={`${dataTest}-cybsButton-icon-text`}
            iconType={iconType}
            customCSS={textButtonStyles}
            {...commonProps}
            data-icon={iconLocation}
          >
            <span aria-hidden={diffLabel || undefined}>{i18n.label}</span>
            {diffLabel && <span className="cybs-a11y-hidden">{ariaLabel}</span>}
          </ButtonIcon>
        );
        break;
      case 'link':
        if (tag === 'button') {
          button = (
            <ButtonLink
              tag={<button type="button" />}
              data-test={`${dataTest}-cybsButton-link`}
              {...commonProps}
            >
              <span aria-hidden={diffLabel || undefined}>{i18n.label}</span>
              {diffLabel && (
                <span className="cybs-a11y-hidden">{ariaLabel}</span>
              )}
            </ButtonLink>
          );
        }
        if (tag === 'a') {
          button = (
            <ButtonLink
              tag={<a href={href}>{i18n.label}</a>}
              data-test={`${dataTest}-cybsButton-anchor`}
              {...commonProps}
              customCSS={linkButtonStyles}
            >
              <span aria-hidden={diffLabel || undefined}>{i18n.label}</span>
              {diffLabel && (
                <span className="cybs-a11y-hidden">{ariaLabel}</span>
              )}
            </ButtonLink>
          );
        }
        break;
      case 'text':
      default:
        button = (
          <ButtonText
            data-test={`${dataTest}-cybsButton-text`}
            {...commonProps}
            customCSS={textButtonStyles}
          >
            {iconLocation === 'right' ? (
              <span aria-hidden={diffLabel || undefined}>{i18n.label}</span>
            ) : null}
            {iconClass && <Icon name={iconClass} resolution={resolution} />}
            {iconLocation === 'left' ? (
              <span aria-hidden={diffLabel || undefined}>{i18n.label}</span>
            ) : null}
            {diffLabel && <span className="cybs-a11y-hidden">{ariaLabel}</span>}
          </ButtonText>
        );
    }
    return button;
  }
);

CybsButton.propTypes = {
  /**
   * Color scheme of button
   */
  colorScheme: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
  /**
   * Data test.
   */
  dataTest: PropTypes.string.isRequired,
  /**
   * If true, the button is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * href for button link.
   */
  href: PropTypes.string,
  /**
   * Localization
   */
  i18n: PropTypes.shape({
    ariaLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    tooltipLabel: PropTypes.string
  }),
  /**
   * Name for the button icon.
   */
  iconClass: PropTypes.string,

  /**
   * Where the button icon should be.
   */
  iconLocation: PropTypes.oneOf(['right', 'left']),

  /**
   * Icon type
   */
  iconType: PropTypes.oneOf([
    'dark', // Not used in years, overwritten by colorscheme
    'dark-outline', // Not used in years
    'dark-tiny', // Not used in years
    'light',
    'light-fill', // Deprecated in WCAG 2.1
    'light-outline',
    'light-tiny'
  ]),

  /**
   * If `true`, icon is active
   */
  isActive: PropTypes.bool,

  /**
   * If `true`, tab is selected.
   */
  isSelected: PropTypes.bool,

  /**
   * If `true`, the button takes the full width of its container.
   */
  isFullWidth: PropTypes.bool,

  /**
   * Callback fired on click.
   */
  onClick: PropTypes.func,
  /**
   *  Callback fired on KeyDown.
   */
  onKeyDown: PropTypes.func,
  /**
   * If `true`, no tooltip will show when hovering on the icon button.
   */
  readOnly: PropTypes.bool,

  /**
   * Resolution for the button icon.
   */
  resolution: PropTypes.oneOf(['high', 'low', 'tiny']),

  /**
   * Tag of button
   */
  tag: PropTypes.oneOf(['a', 'button', 'div', 'span']),

  /**
   * Emotion styling function for the tooltip.
   */
  tooltipCustomCSS: PropTypes.func,

  /**
   * How many milliseconds to wait before showing the tooltip.
   */
  tooltipDelayOn: PropTypes.number,

  /**
   * Display orientation for the tooltip.
   */
  tooltipDirection: PropTypes.string,

  /**
   * Unique id to distinguish tooltips from each other.
   */
  tooltipId: PropTypes.string,
  /**
   * Trigger type for showing the tooltip.
   */
  tooltipShowOn: PropTypes.oneOf(
    'click',
    'right-click',
    'hover',
    'focus',
    'focus-hover'
  ),

  /**
   * Category of button
   */
  variant: PropTypes.oneOf([
    'icon',
    'link',
    'split',
    'text',
    'tab',
    'text-icon'
  ]),

  /**
   * for wrapping link text in containers
   */
  width: PropTypes.string,
  /**
   * Prevent focus on the icon button.
   */
  prenventIconFocus: PropTypes.bool
};

CybsButton.defaultProps = {
  isActive: false,
  colorScheme: 'primary',
  disabled: false,
  i18n: {
    ariaLabel: '',
    label: 'Button',
    tooltipLabel: ''
  },
  iconClass: '',
  iconLocation: 'right',
  iconType: undefined,
  isFullWidth: false,
  href: '',
  onClick: () => {},
  onKeyDown: () => {},
  readOnly: false,
  resolution: 'low',
  isSelected: false,
  tag: 'button',
  tooltipCustomCSS: () => {},
  tooltipDelayOn: undefined,
  tooltipDirection: 'top',
  tooltipId: undefined,
  tooltipShowOn: 'focus-hover',
  variant: 'text',
  width: '',
  prenventIconFocus: true
};

CybsButton.displayName = 'CybsButton';

export default CybsButton;
